import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  allowedDomainErrorMessage,
  useAddOrgAllowedDomain,
  useOrgAllowedDomains,
  useRemoveOrgAllowedDomain,
  validateDomainInput,
} from "@/hooks/useOrgAllowedDomains";
import { cn } from "@/lib/utils";
import { AtSign, Plus, X } from "lucide-react";

interface AllowedDomainsEditorProps {
  organizationId: string;
  /**
   * The organization signs in with Microsoft Entra — allowed domains can't be used, so the editor
   * is locked with the explanation up front. A refused add (`DOMAINS_BLOCKED_FOR_ENTRA`) still
   * locks it as a fallback when this flag is unknown or stale.
   */
  hasEntraIntegration?: boolean;
  /**
   * Whether the organization signs in with Entra is not known yet (`loading`) or could not be
   * checked (`failed`). The editor stays locked rather than presenting the org as non-Entra.
   */
  entraCheck?: "loading" | "failed";
}

const ENTRA_CODE = "DOMAINS_BLOCKED_FOR_ENTRA";

/**
 * Allowed email domains, managed on the client's behalf. Exact hosts only; the server enforces
 * the free-provider blocklist, cross-org uniqueness, the per-org limit and the Entra block, and
 * this editor explains each refusal inline.
 */
export function AllowedDomainsEditor({ organizationId, hasEntraIntegration = false, entraCheck }: AllowedDomainsEditorProps) {
  const { data: domains, isLoading, isError } = useOrgAllowedDomains(organizationId);
  const addDomain = useAddOrgAllowedDomain(organizationId);
  const removeDomain = useRemoveOrgAllowedDomain(organizationId);

  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [entraRefused, setEntraRefused] = useState(false);
  const entraBlocked = hasEntraIntegration || entraRefused;
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  const handleAdd = () => {
    const result = validateDomainInput(
      value,
      domains?.map((d) => d.domain),
    );
    if ("error" in result) {
      setError(result.error);
      return;
    }
    setError(null);
    addDomain.mutate(result.domain, {
      onSuccess: () => setValue(""),
      onError: (err: unknown) => {
        if ((err as { errorCode?: string })?.errorCode === ENTRA_CODE) {
          setEntraRefused(true);
        }
        setError(allowedDomainErrorMessage(err));
      },
    });
  };

  const inputDisabled = entraBlocked || !!entraCheck || addDomain.isPending;

  return (
    <div className="space-y-3" data-testid="allowed-domains-editor">
      <div>
        <h4 className="font-medium flex items-center gap-2">
          <AtSign className="h-4 w-4" />
          Allowed email domains
        </h4>
        <p className="text-sm text-muted-foreground">
          Anyone with an email on these domains can register as an employee and sign in
        </p>
      </div>

      {entraBlocked && (
        <p
          className="text-sm rounded-md border bg-muted/50 p-3"
          data-testid="allowed-domains-entra-note"
        >
          This organization signs in with Microsoft Entra, so its employees come from Entra and
          allowed domains can&apos;t be used.
        </p>
      )}

      {!entraBlocked && entraCheck === "failed" && (
        <p
          className="text-sm rounded-md border bg-muted/50 p-3"
          data-testid="allowed-domains-entra-unknown"
        >
          We couldn&apos;t check whether this organization signs in with Microsoft Entra, so
          domains can&apos;t be added right now. Refresh the page to try again.
        </p>
      )}

      <form
        className="flex items-start gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          handleAdd();
        }}
      >
        <div className="flex-1 space-y-1">
          <Label htmlFor="allowed-domain-input" className="sr-only">
            Allowed email domain
          </Label>
          <Input
            id="allowed-domain-input"
            value={value}
            placeholder="e.g. company.com"
            onChange={(e) => {
              setValue(e.target.value);
              if (error && !entraBlocked) setError(null);
            }}
            aria-invalid={!!error}
            aria-describedby={error ? "allowed-domain-error" : undefined}
            disabled={inputDisabled}
          />
          {error && (
            <p id="allowed-domain-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <Button type="submit" variant="outline" disabled={inputDisabled || !value.trim()}>
          <Plus className="h-4 w-4 mr-1" />
          {addDomain.isPending ? "Adding..." : "Add domain"}
        </Button>
      </form>

      {isLoading ? (
        <div className="space-y-2" data-testid="allowed-domains-loading">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          Couldn&apos;t load the allowed domains. Refresh the page to try again.
        </p>
      ) : !domains || domains.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="allowed-domains-empty">
          No allowed domains yet — only imported or invited employees can sign in.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="allowed-domains-list">
          {domains.map((d) => (
            <li
              key={d.id ?? d.domain}
              className={cn("flex items-center justify-between rounded-md border px-3 py-2")}
            >
              <span className="font-mono text-sm">{d.domain}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove ${d.domain}`}
                disabled={removeDomain.isPending}
                onClick={() => d.domain && setPendingRemoval(d.domain)}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog
        open={!!pendingRemoval}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pendingRemoval}?</AlertDialogTitle>
            <AlertDialogDescription>
              New people with an @{pendingRemoval} email will no longer be able to register.
              Employees who already registered keep their accounts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingRemoval) removeDomain.mutate(pendingRemoval);
                setPendingRemoval(null);
              }}
            >
              Remove domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
